import { attr, belongsTo } from "@ember-data/model";
import type Store from "@ember-data/store";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { validatePresence } from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import type Patient from "parog-web/models/patient";
import type { TreatmentProtocol } from "parog-web/models/protocol";
import JournalEventModel from "./journal-event";

export class Question {
  @tracked answer: string;
  constructor(
    public type: "severity" | "perturbation" | "frequency" | "yesno",
    public short: string,
    public title: string,
    answer = ""
  ) {
    this.answer = answer;
  }

  deepcopy() {
    return new Question(this.type, this.short, this.title, this.answer);
  }
}

export class Section {
  @tracked declare questions: Array<Question>;

  constructor(
    public id: string,
    public key: string,
    public title: string,
    questions: Question[],
    public pointColor?: string
  ) {
    this.questions = questions;
  }

  deepcopy() {
    const section = new Section(
      this.id,
      this.key,
      this.title,
      [],
      this.pointColor
    );
    section.questions = this.questions.map(question => question.deepcopy());
    return section;
  }
}

export default class CtcaeFilledJournalEvent extends JournalEventModel {
  @service declare store: Store;

  static type = "ctcae-filled-journal-event";
  /**
   * the list of sections containing question and answer
   */
  @attr() declare sections: any[];

  @attr("string") declare originTaskIdentifier: string;

  static validations(intl: IntlService) {
    return {
      occurredAt: validatePresence({
        presence: true,
        description: intl.t("validations.occurred-at")
      })
    };
  }

  @belongsTo("patient", {
    async: false,
    inverse: "ctcaeFilledJournalEvents"
  })
  declare patient: Patient;

  // Do not modify the order of this array
  static sections: Section[] = [
    new Section("1", "dry-mouth", "Sécheresse de la bouche", [
      new Question(
        "severity",
        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre sécheresse de la bouche à son pire degré ?"
      )
    ]),
    new Section("2", "difficulty-swallowing", "Difficulté à avaler", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre difficulté à avaler à son pire degré ?"
      )
    ]),
    new Section("3", "mouth-throat-sores", "Ulcères de la bouche/gorge", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos ulcères de la bouche ou de la gorge à leur pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point des ulcères de la bouche ou de la gorge ont-ils perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section(
      "4",
      "cracking-mouth-corners",
      "Gerçures aux commissures des lèvres (perlèche/chéilite)",
      [
        new Question(
          "severity",

          "Sévérité",
          "Au cours des 7 derniers jours, quelle a été la sévérité des gerçures aux commissures de vos lèvres à leur pire degré ?"
        )
      ]
    ),
    new Section(
      "5",
      "voice-quality-changes",
      "Altérations de la qualité de la voix",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu des altérations de la voix ?"
        )
      ]
    ),
    new Section("6", "hoarseness", "Enrouement", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre enrouement à son pire degré ?"
      )
    ]),
    new Section("7", "taste-changes", "Altérations du goût", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos problèmes pour sentir le goût des aliments ou des boissons à leur pire degré ?"
      )
    ]),
    new Section("8", "decreased-appetite", "Diminution de l'appétit", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de la diminution de votre appétit à son pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 d}erniers jours, à quel point la diminution de votre appétit a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("9", "nausea", "Nausées", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos nausées à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des nausées ?"
      )
    ]),
    new Section("10", "vomiting", "Vomissements", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos vomissements à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des vomissements ?"
      )
    ]),
    new Section("11", "heartburn", "Brûlures d'estomac", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos brûlures d'estomac à leur pire degré"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des brûlures d'estomac ?"
      )
    ]),
    new Section("12", "gas", "Gaz", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu plus de gaz (flatulences) ?"
      )
    ]),
    new Section("13", "bloating", "Ballonnements", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos ballonnements dans l'abdomen (le ventre) à leur pire degré"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 dern}iers jours, à quelle fréquence avez-vous eu des ballonnements dans l'abdomen (le ventre)"
      )
    ]),
    new Section("14", "hiccups", "Hoquet", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre hoquet à son pire degré"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 dern}iers jours, à quelle fréquence avez-vous eu le hoquet ?"
      )
    ]),
    new Section("15", "constipation", "Constipation", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre constipation à son pire degré"
      )
    ]),
    new Section("16", "diarrhea", "Diarrhée", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des selles molles ou liquides (diarrhée) ?"
      )
    ]),
    new Section("17", "abdominal-pain", "Douleur abdominale", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu une douleur dans l'abdomen (région du ventre) ?"
      ),
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre douleur dans l'abdomen (région du ventre) à son pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point une douleur dans l'abdomen (région du ventre) a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("18", "fecal-incontinence", "Incontinence fécale", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous perdu le contrôle de vos selles ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point la perte de contrôle de vos selles a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("19", "shortness-of-breath", "Essoufflement", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre essoufflement à son pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point un essoufflement a-t-il perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("20", "cough", "Toux", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre toux à son pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point votre toux a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("21", "wheezing", "Sifflement", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre sifflement (bruit de sifflement dans la poitrine en respirant) à son pire degré"
      )
    ]),
    new Section("22", "swelling", "Gonflement", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité du gonflement de vos bras ou de vos jambes à son pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu un gonflement de vos bras ou de vos jambes ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point un gonflement de vos bras ou de vos jambes a-t-il perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("23", "heart-palpitations", "Palpitations du cœur", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos battements de cœur forts ou rapides (palpitations) à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous senti des battements de cœur forts ou rapides (palpitations) ?"
      )
    ]),
    new Section("24", "rash", "Éruption cutanée", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu une éruption cutanée ?"
      )
    ]),
    new Section("25", "skin-dryness", "Sécheresse de la peau", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre sécheresse de la peau à son pire degré"
      )
    ]),
    new Section("26", "acne", "Acné", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre acné ou des boutons sur votre visage ou votre torse à leur pire degré ?"
      )
    ]),
    new Section("27", "hair-loss", "Perte de cheveux", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, avez-vous eu une perte de cheveux ?"
      )
    ]),
    new Section("28", "itching", "Démangeaisons", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos démangeaisons à leur pire degré ?"
      )
    ]),
    new Section("29", "hives", "Urticaire", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu de l'urticaire (boutons rouges qui démangent sur la peau) ?"
      )
    ]),
    new Section("30", "hand-foot-syndrome", "Syndrôme main-pied", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre syndrôme main-pied (éruption cutanée sur les mains ou les pieds qui peut entraîner des gerçures, une desquamation, une rougeur ou une douleur) à son pire degré ?"
      )
    ]),
    new Section("31", "nail-loss", "Perte d’ongles", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous perdu des ongles des doigts ou des orteils ?"
      )
    ]),
    new Section("32", "nail-ridging", "Ongles striés", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu des striures ou des bosses sur les ongles de vos doigts ou de vos orteils ?"
      )
    ]),
    new Section("33", "nail-discoloration", "Décoloration des ongles", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu une altération de la couleur des ongles de vos doigts ou de vos orteils ?"
      )
    ]),
    new Section(
      "34",
      "sensitivity-to-sunlight",
      "Sensibilité à la lumière du soleil",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu une plus grande sensibilité de la peau à la lumière du soleil ?"
        )
      ]
    ),
    new Section(
      "35",
      "bed-pressure-sores",
      "Escarres de décubitus/plaies de lit",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu des escarres (plaies de lit) ?"
        )
      ]
    ),
    new Section(
      "36",
      "radiation-skin-reaction",
      "Réaction cutanée au rayonnement",
      [
        new Question(
          "severity",

          "Sévérité",
          "Au cours des 7 derniers jours, quelle a été la sévérité de vos brûlures cutanées dues au rayonnement à leur pire degré ?"
        )
      ]
    ),
    new Section("37", "skin-darkening", "Assombrissement de la peau", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu un assombrissement inhabituel de la peau ?"
      )
    ]),
    new Section("38", "stretch-marks", "Vergetures", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu un assombrissement inhabituel de la peau ?"
      )
    ]),
    new Section(
      "39",
      "numbness-tingling",
      "Engourdissements et fourmillements",
      [
        new Question(
          "severity",

          "Sévérité",
          "Au cours des 7 derniers jours, quelle a été la sévérité des engourdissements ou fourmillements dans vos mains ou vos pieds à leur pire degré ?"
        ),
        new Question(
          "perturbation",

          "Perturbation",
          "Au cours des 7 derniers jours, à quel point des engourdissements ou fourmillements dans vos mains ou vos pieds ont-ils perturbé vos activités habituelles ou quotidiennes ?"
        )
      ]
    ),
    new Section("40", "dizziness", "Vertiges", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos vertiges à leur pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point vos vertiges ont-ils perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("41", "blurred-vision", "Vision trouble", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre vision trouble à son pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point votre vision trouble a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("42", "flashing-lights", "Lumières clignotantes", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu des lumières clignotantes devant vos yeux?"
      )
    ]),
    new Section("43", "visual-floaters", "Corps flottants devant les yeux", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu des points ou des lignes (corps flottants) qui dansent devant vos yeux?"
      )
    ]),
    new Section("44", "watery-eyes", "Yeux larmoyants", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité du larmoiement de vos yeux à son pire degré?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point vos yeux larmoyants (larmoiement) ont-ils perturbé vos activités habituelles ou quotidiennes?"
      )
    ]),
    new Section("45", "ringing-in-ears", "Bourdonnement dans les oreilles", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité du bourdonnement dans vos oreilles à son pire degré?"
      )
    ]),
    new Section("46", "concentration", "Concentration", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos problèmes de concentration à leur pire degré?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point des problèmes de concentration ont-ils perturbé vos activités habituelles ou quotidiennes?"
      )
    ]),
    new Section("47", "memory", "Mémoire", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos problèmes de mémoire à leur pire degré ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point des problèmes de mémoire ont-ils perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("48", "general-pain", "Douleur générale", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre douleur à son pire degré"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous ressenti une douleur ?"
      )
    ]),
    new Section("49", "headache", "Maux de tête", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos maux de tête à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu mal à la tête ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point vos maux de tête ont-ils perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("50", "muscle-pain", "Douleur musculaire", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos douleurs musculaires à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des douleurs musculaires ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point vos douleurs musculaires ont-elles perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("51", "joint-pain", "Douleur articulaire", [
      new Question(
        "severity",

        "Sévérité",
        "À quelle fréquence avez-vous eu des douleurs dans les articulations?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des douleurs dans les articulations (par exemple les coudes, les genoux, les épaules) ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "À quel point vos} douleurs dans les articulations ont-elles perturbé vos activités habituelles ou quotidiennes?"
      )
    ]),
    new Section("52", "insomnia", "Insomnie", [
      new Question(
        "severity",

        "Sévérité",
        "Quelle a été la sévérité de vos insomnies à leur pire degré?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "À quel point vos} insomnies ont-elles perturbé vos activités habituelles ou quotidiennes?"
      )
    ]),
    new Section("53", "fatigue", "Fatigue", [
      new Question(
        "severity",

        "Sévérité",
        "Quelle a été la sévérité de votre fatigue à son pire degré?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "À quel point une fatigue, une lassitude ou un manque d'énergie ont-ils perturbé vos activités habituelles ou quotidiennes?"
      )
    ]),
    new Section("54", "anxious", "Anxiété", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre anxiété à son pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous ressenti de l'anxiété ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point votre anxiété a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("55", "discouraged", "Découragement", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos sentiments que rien ne pouvait vous remonter le moral à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu le sentiment que rien ne pouvait vous remonter le moral"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point le sentiment que rien ne pouvait vous remonter le moral a-t-il perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("56", "sad", "Tristesse", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos sentiments de tristesse ou de malheur à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence vous êtes-vous senti(e) triste ou malheureux(se) ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point vos sentiments de tristesse ou de malheur ont-ils perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section(
      "57",
      "irregular-periods-vaginal-bleeding",
      "Règles irrégulières/saignements vaginaux",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu des règles irrégulières ?"
        )
      ]
    ),
    new Section(
      "58",
      "missed-expected-menstrual-period",
      "Absence de règles à la date prévue",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu une absence de règles à la date prévue ?"
        )
      ]
    ),
    new Section("59", "vaginal-discharge", "Pertes vaginales", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu des pertes vaginales inhabituelles ?"
      )
    ]),
    new Section("60", "vaginal-dryness", "Sécheresse vaginale", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre sécheresse vaginale à son pire degré"
      )
    ]),
    new Section("61", "painful-urination", "Miction douloureuse", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre douleur ou brûlure pendant que vous uriniez à son pire degré ?"
      )
    ]),
    new Section("62", "urinary-urgency", "Urgence urinaire", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous senti un besoin urgent et soudain d'uriner ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point des besoins urgents et soudains d'uriner ont-ils perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section("63", "urinary-frequency", "Fréquence urinaire", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, vous est-il arrivé d’avoir besoin d’uriner fréquemment ?"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point votre fréquence urinaire a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section(
      "64",
      "change-in-usual-urine-color",
      "Altération de la couleur habituelle de l'urine",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu une altération de la couleur de votre urine ?"
        )
      ]
    ),
    new Section("65", "urinary-incontinence", "Incontinence urinaire", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous perdu le contrôle de votre urine (fuites)"
      ),
      new Question(
        "perturbation",

        "Perturbation",
        "Au cours des 7 derniers jours, à quel point la perte de contrôle de votre urine (fuites) a-t-elle perturbé vos activités habituelles ou quotidiennes ?"
      )
    ]),
    new Section(
      "66",
      "achieve-and-maintain-erection",
      "Obtention et maintien d'une érection",
      [
        new Question(
          "severity",

          "Sévérité",
          "Au cours des 7 derniers jours, quelle a été la sévérité de votre difficulté à obtenir ou à maintenir une érection à son pire degré ?"
        )
      ]
    ),
    new Section("67", "ejaculation", "Éjaculation", [
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous eu des problèmes d'éjaculation ?"
      )
    ]),
    new Section("68", "decreased-libido", "Baisse de la libido", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre perte d'intérêt sexuel à son pire degré ?"
      )
    ]),
    new Section("69", "delayed-orgasm", "Orgasme retardé", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu l'impression qu'il fallait trop longtemps pour avoir un orgasme ou pour jouir ?"
      )
    ]),
    new Section("70", "unable-to-have-orgasm", "Incapable d'avoir un orgasme", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous été incapable d'avoir un orgasme ou de jouir"
      )
    ]),
    new Section(
      "71",
      "pain-w-sexual-intercourse",
      "Douleur pendant les rapports sexuels",
      [
        new Question(
          "severity",

          "Sévérité",
          "Au cours des 7 derniers jours, quelle a été la sévérité de votre douleur pendant des rapports sexuels vaginaux à son pire degré ?"
        )
      ]
    ),
    new Section(
      "72",
      "breast-swelling-tenderness",
      "Gonflement et sensibilité des seins",
      [
        new Question(
          "severity",

          "Sévérité",
          "Au cours des 7 derniers jours, quelle a été la sévérité du grossissement ou de la sensibilité de vos seins à leur pire degré ?"
        )
      ]
    ),
    new Section("73", "bruising", "Ecchymoses", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous facilement eu des ecchymoses (marques noires et bleues) ?"
      )
    ]),
    new Section("74", "chills", "Frissons", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de vos frissonnements ou tremblements de froid à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "Au cours des 7 derniers jours, à quelle fréquence avez-vous frissonné ou tremblé de froid ?"
      )
    ]),
    new Section(
      "75",
      "increased-sweating",
      "Augmentation de la transpiration",
      [
        new Question(
          "severity",

          "Sévérité",
          "Quelle a été la sévérité de votre transpiration inattendue ou excessive pendant la journée ou la nuit (sans lien avec des bouffées de chaleur) à son pire degré"
        ),
        new Question(
          "frequency",

          "Fréquence",
          "À quelle fréquence avez-vous eu une transpiration inattendue ou excessive pendant la journée ou la nuit (sans lien avec des bouffées de chaleur) ?"
        )
      ]
    ),
    new Section("76", "decreased-sweating", "Diminution de la transpiration", [
      new Question(
        "yesno",

        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu une diminution inattendue de la transpiration ?"
      )
    ]),
    new Section("77", "hot-flashes", "Bouffées de chaleur", [
      new Question(
        "severity",

        "Sévérité",
        "Quelle a été la sévérité de vos bouffées de chaleur à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "À quelle fréquence avez-vous eu des bouffées de chaleur ?"
      )
    ]),
    new Section("78", "nosebleed", "Saignement de nez", [
      new Question(
        "severity",

        "Sévérité",
        "Quelle a été la sévérité de vos saignements de nez à leur pire degré ?"
      ),
      new Question(
        "frequency",

        "Fréquence",
        "À quelle fréquence avez-vous eu des saignements de nez ?"
      )
    ]),
    new Section(
      "79",
      "pain-and-swelling-at-injection-site",
      "Douleur et gonflement au site d'injection",
      [
        new Question(
          "yesno",

          "Apparition",
          "Au cours des 7 derniers jours, avez-vous eu une douleur, un gonflement ou une rougeur sur un site d'injection médicamenteuse ou iv ?"
        )
      ]
    ),
    new Section("80", "body-odor", "Odeur corporelle", [
      new Question(
        "severity",

        "Sévérité",
        "Au cours des 7 derniers jours, quelle a été la sévérité de votre odeur corporelle à son pire degré ?"
      )
    ]),
    new Section("81", "blood", "Saignement", [
      new Question("yesno", "Apparition", "Avez-vous observé des saignements ?")
    ]),
    new Section(
      "82",
      "treatment-modification",
      "Modification du traitement habituel",
      [
        new Question(
          "yesno",
          "Apparition",
          "Votre traitement habituel a-t-il été changé récemment ?"
        )
      ]
    ),
    new Section("83", "blood-in-urine", "Sang dans les urines", [
      new Question(
        "yesno",
        "Apparition",
        "Avez-vous observé du sang dans vos urines ?"
      )
    ]),
    new Section("84", "scar-red", "Aspect de la cicatrice", [
      new Question(
        "yesno",
        "Apparition",
        "Votre cicatrice présente-t-elle un aspect rouge ?"
      )
    ]),
    new Section("85", "scar-hot", "Cicatrice chaude au toucher", [
      new Question(
        "yesno",
        "Apparition",
        "Votre cicatrice est-elle chaude au toucher ?"
      )
    ]),
    new Section("86", "scar-swell", "Cicatrice gonflée", [
      new Question("yesno", "Apparition", "Votre cicatrice est-elle gonflée ?")
    ]),
    new Section("87", "scar-flow", "Cicatrice avec écoulements", [
      new Question(
        "yesno",
        "Apparition",
        "Avez-vous remarqué un écoulement au niveau des incisions de votre cicatrice ?"
      )
    ]),
    new Section("88", "scar-bruise", "Cicatrice avec hématomes", [
      new Question(
        "yesno",
        "Apparition",
        "Avez-vous remarqué un ou des hématomes au niveau des incisions ?"
      )
    ]),
    new Section("89", "fever", "Fièvre", [
      new Question(
        "yesno",
        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu de la fièvre ?"
      )
    ]),
    new Section("90", "vein-thrombosis", "Thrombose veineuse", [
      new Question(
        "yesno",
        "Apparition",
        "Au cours des 7 derniers jours, avez-vous eu des gonflements au niveau des mollets et des cuisses accompagnés d'une sensation de lourdeur ?"
      )
    ])
  ];

  static currentSections(
    patient: Patient,
    store: Store,
    originJournalEventId?: string,
    originJournalEventType?: string
  ): Section[] {
    if (
      Boolean(originJournalEventType) &&
      originJournalEventType == "TaskPlannedJournalEvent" &&
      Boolean(originJournalEventId)
    ) {
      const task = store.peekRecord(
        "task-planned-journal-event",
        Number(originJournalEventId)
      );
      if (task && task.protocolName) {
        return this.sectionsConsideringTreatmentName(
          task.protocolName as TreatmentProtocol
        );
      }
    }

    const protocolNames =
      patient.treatmentHistoryChangedJournalEvent?.activeProtocols.map(
        activeProtocol => activeProtocol.protocol
      );
    if (!protocolNames) {
      return [];
    }

    return this.intersectSections(
      protocolNames.map(protocolName =>
        this.sectionsConsideringTreatmentName(protocolName)
      )
    );
  }

  static sectionsConsideringTreatmentName(
    treatmentName: TreatmentProtocol
  ): Section[] {
    const olaparibSections = [
      "3",
      "7",
      "8",
      "9",
      "16",
      "17",
      "20",
      "40",
      "49",
      "53"
    ];

    const regorafenibStivargaSections = [
      "3",
      "5",
      "8",
      "9",
      "16",
      "30",
      "53",
      "81",
      "82"
    ];

    const prostatectomyAndAdtSections = [
      "17",
      "48",
      "53",
      "62",
      "83",
      "84",
      "85",
      "86",
      "87",
      "88"
    ];

    const treatmentSectionsMap: Record<string, string[]> = {
      "olaparib-initial": olaparibSections,
      "olaparib-following": olaparibSections,
      "regorafenib-stivarga-initial": regorafenibStivargaSections,
      "regorafenib-stivarga-renewal": regorafenibStivargaSections,
      "prostatectomy-total": prostatectomyAndAdtSections,
      "adt-anti-androgen": prostatectomyAndAdtSections,
      "adt-chemo": prostatectomyAndAdtSections,
      "abemaciclib-verzenios": [
        "9",
        "10",
        "16",
        "20",
        "24",
        "25",
        "40",
        "53",
        "89"
      ],
      "ribociclib-kisqali": ["9", "10", "16", "20", "24"]
    };

    const sectionIds = treatmentSectionsMap[treatmentName] || [];
    return this.sections
      .filter(section => sectionIds.includes(section.id))
      .map(section => section.deepcopy());
  }

  get baseSections(): Section[] {
    return CtcaeFilledJournalEvent.currentSections(this.patient, this.store);
  }

  get sectionsWithAnswers(): Section[] {
    // We will transform every section stored in the JE, so for each ID
    return Object.keys(this.sections).map(sectionId => {
      // We retrieve the section with all the other informations
      const section = CtcaeFilledJournalEvent.sections.find(
        _section => _section.id == sectionId
      ) as Section;
      // We deepcopy it
      const sectionToReturn = new Section(
        section.id,
        section.key,
        section.title,
        section.questions,
        section.pointColor
      );
      sectionToReturn.questions = section.questions.map(
        q => new Question(q.type, q.short, q.title, q.answer)
      );
      // We retrieve the saved section
      const savedSection = this.sections[sectionId];
      // We set the answers

      sectionToReturn.pointColor = "green";

      sectionToReturn.questions.forEach(question => {
        question.answer = savedSection?.[question.type];

        if (
          question.answer === "4" ||
          (question.type == "yesno" && question.answer === "1")
        ) {
          sectionToReturn.pointColor = "red";
        }
        if (question.answer === "3" && sectionToReturn.pointColor != "red") {
          sectionToReturn.pointColor = "orange";
        }
      });

      return sectionToReturn;
    });
  }

  static intersectSections(sections: Section[][]) {
    if (sections.length === 0) {
      return [];
    }
    let result = sections[0];
    for (let i = 1; i < sections.length; i++) {
      const temp: Section[] = [];
      for (const obj of result) {
        if (sections[i].some(o => o.id === obj.id)) {
          temp.push(obj);
        }
      }
      result = temp;
    }
    return result;
  }

  static typeAndValueToLabel(type: string, value: string): string {
    if (type == "frequency") {
      switch (value) {
        case "0":
          return "Jamais";
        case "1":
          return "Rarement";
        case "2":
          return "Occasionnellement";
        case "3":
          return "Fréquemment";
        case "4":
          return "Presque constamment";
      }
    } else if (type == "severity") {
      switch (value) {
        case "0":
          return "Aucune";
        case "1":
          return "Légère";
        case "2":
          return "Modérée";
        case "3":
          return "Sévère";
        case "4":
          return "Très sévère";
      }
    } else if (type == "perturbation") {
      switch (value) {
        case "0":
          return "Pas du tout";
        case "1":
          return "Un petit peu";
        case "2":
          return "Assez";
        case "3":
          return "Beaucoup";
        case "4":
          return "Énormément";
      }
    } else {
      switch (value) {
        case "0":
          return "Non";
        case "1":
          return "Oui";
      }
    }
    return "";
  }

  static typeToLabel(type: string): string {
    switch (type) {
      case "frequency":
        return "Fréquence";
      case "severity":
        return "Sévérité";
      case "perturbation":
        return "Perturbation";
      default:
        return "Apparition";
    }
  }

  static typeToValue(type: string): Array<Record<string, string>> {
    switch (type) {
      case "frequency":
        return [
          {
            label: "Jamais",
            value: "0"
          },
          {
            label: "Rarement",
            value: "1"
          },
          {
            label: "Occasionnellement",
            value: "2"
          },
          {
            label: "Fréquemment",
            value: "3"
          },
          {
            label: "Presque constamment",
            value: "4"
          }
        ];
      case "severity":
        return [
          {
            label: "Aucune",
            value: "0"
          },
          {
            label: "Légère",
            value: "1"
          },
          {
            label: "Modérée",
            value: "2"
          },
          {
            label: "Sévère",
            value: "3"
          },
          {
            label: "Très sévère",
            value: "4"
          }
        ];
      case "perturbation":
        return [
          {
            label: "Pas du tout",
            value: "0"
          },
          {
            label: "Un petit peu",
            value: "1"
          },
          {
            label: "Assez",
            value: "2"
          },
          {
            label: "Beaucoup",
            value: "3"
          },
          {
            label: "Énormément",
            value: "4"
          }
        ];
      case "yesno":
        return [
          {
            label: "Non",
            value: "0"
          },
          {
            label: "Oui",
            value: "1"
          }
        ];
      default:
        return [];
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "ctcae-filled-journal-event": CtcaeFilledJournalEvent;
  }
}
